import { faExclamation, faX } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Modal, ModalContent } from '@pickleballinc/react-ui';

import Switch from '@/components/Switch';

interface ConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message?: string;
  confirmButtonText?: string;
  confirmButtonVariant?: 'primary' | 'secondary';
  rollbackZeroScores?: boolean;
  setRollbackZeroScores?: React.Dispatch<React.SetStateAction<boolean>>;
  doNotShowIcon?: boolean;
  iconColor?: string;
  doNotShowCloseButton?: boolean;
  showEachSentenceInNewLine?: boolean;
}

const ConfirmationModal = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmButtonText = 'Ok',
  confirmButtonVariant = 'primary',
  iconColor = 'yellow',
  rollbackZeroScores,
  setRollbackZeroScores,
  doNotShowIcon = false,
  doNotShowCloseButton = false,
  showEachSentenceInNewLine = false,
}: ConfirmationModalProps) => {
  return (
    <Modal open={open} onOpenChange={onClose}>
      <ModalContent
        className={`flex w-[calc(100%-16px)] flex-col gap-4 !overflow-hidden !p-6 !outline-none ${showEachSentenceInNewLine ? 'md:max-w-[480px]' : 'md:max-w-[430px]'}`}
      >
        <div className='relative text-center'>
          <div
            className='absolute -right-2 top-0 flex size-6 items-center justify-center rounded-md border border-solid border-gray-300 bg-white p-1 hover:cursor-pointer hover:bg-gray-100'
            onClick={onClose}
          >
            <FontAwesomeIcon icon={faX} className='text-gray-400' height={20} />
          </div>
          {!doNotShowIcon && (
            <div
              className={`${`border-${iconColor}`} mx-auto flex size-16 items-center justify-center rounded-full border-4 md:size-24`}
            >
              <FontAwesomeIcon icon={faExclamation} className={`text-${iconColor}`} size='3x' />
            </div>
          )}
        </div>
        <div className='flex flex-col gap-2'>
          <div className='text-center text-xl font-semibold md:text-2xl'>{title}</div>
          {message ? (
            <div className='text-center text-sm md:text-lg'>
              {showEachSentenceInNewLine
                ? message.split('.').map(
                    (sentence, index) =>
                      sentence.trim() && (
                        <div key={`sentence-${index}`} className='mb-2 text-center'>
                          {sentence}.
                        </div>
                      )
                  )
                : message}
            </div>
          ) : null}
          {rollbackZeroScores !== undefined && setRollbackZeroScores ? (
            <div className='flex items-center justify-center'>
              <Switch
                defaultChecked={rollbackZeroScores}
                onChange={() => setRollbackZeroScores(!rollbackZeroScores)}
                borderColor='border-leagues-300/90'
                bgColor='bg-leagues-300/90'
              />
              <span className='ml-4 text-base md:text-lg'>
                {rollbackZeroScores ? 'Remove all scores' : 'Keep all scores'}
              </span>
            </div>
          ) : null}
        </div>
        <div
          className={`flex items-center gap-4 ${!doNotShowCloseButton ? 'justify-between' : 'justify-center'}`}
        >
          {!doNotShowCloseButton && (
            <Button
              variant='secondary'
              className='flex w-[200px] justify-center border !text-sm !text-gray-500 focus:!shadow-[unset]'
              onClick={onClose}
            >
              Close
            </Button>
          )}
          <Button
            variant={confirmButtonVariant}
            className='flex w-[200px] justify-center !text-sm focus:!shadow-[unset] !bg-leagues-300'
            onClick={onConfirm}
          >
            {confirmButtonText}
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default ConfirmationModal;
